import { Directive, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { pluck, takeUntil, tap, map } from 'rxjs/operators';

import { CircleMarker, FeatureGroup, LatLng, Layer, Polyline } from 'leaflet';
// Models
import { AutopilotCommand, StageGroup } from '@mocca-technologies/model';
// Services
import { AutopilotService } from 'src/app/services/ap-operations.service';
// Pages
import { LayersControl } from '../pages/ap-operations-page/ap-operations.page';
// Utils
import { environment } from 'src/environments/environment';

class ExploreNode {
    explored: Boolean;
    command: AutopilotCommand;

    constructor(command: AutopilotCommand) {
        this.explored = false;
        this.command = command;
    }

    get latlng(): LatLng {
        const point = this.command.point;
        return new LatLng(point.latitude, point.longitude);
    }
}

@Directive({
    selector: '[drawSelectedTrajectory]'
})
export class DrawSelectedTrajectory implements OnInit, OnDestroy {
    private destroy = new Subject<void>();
    private commands: ExploreNode[];
    private stages: Map<string, StageGroup>;

    @Input() layersControl: LayersControl;
    @Output() onDraw = new EventEmitter<void>();
    @Output() onCenterMap = new EventEmitter<void>();

    constructor(private autopilotService: AutopilotService) {}

    ngOnInit() {
        this.autopilotService.trajectorySelected$
            .pipe(
                takeUntil(this.destroy),
                pluck('allCommandList'),
                map((commands) => commands.map((command) => new ExploreNode(command))),
                tap((commands) => {
                    this.commands = commands;
                })
            )
            .subscribe();


        this.autopilotService.trajectorySelected$
            .pipe(
                takeUntil(this.destroy),
                pluck('stageMap'),
                map((stages) => {
                    this.stages = stages;

                    // Clear older paths
                    Object.values(this.layersControl.overlays).forEach((layer) => {
                        layer.clearLayers();
                    });
                    this.layersControl.overlays = {};


                    this.drawRoutes();

                    this.onDraw.emit();
                })
            )
        .subscribe();
    }

    clearMap() {
        // Clear older paths
        Object.values(this.layersControl.overlays).forEach((layer) => {
            layer.clearLayers();
        });
        this.layersControl.overlays = {};
        !environment.production && console.log("Center map")
        this.onCenterMap.emit();
    }

    ngOnDestroy() {
        this.destroy.next();
    }

    private getAdjacentsTo(target: ExploreNode): ExploreNode[] {
        return this.commands.filter(({ command }) => {
            const targetIdx = target.command.index;
            const isNext = command.nextCommandIdx === targetIdx;
            const isPrev = command.prevCommandIdx === targetIdx;
            const isPrevAmongOthers = command.prevCommandIdxList?.includes(targetIdx);

            return isNext || isPrev || isPrevAmongOthers;
        });
    }

    private drawAsLabeledLayer(stageLabel: string, layer: Layer): void {
        if (stageLabel in this.layersControl.overlays) {
            this.layersControl.overlays[stageLabel].addLayer(layer);
        } else {
            this.layersControl.overlays[stageLabel] = new FeatureGroup([layer]);
        }
    }

    private drawRoutes(): void {

        for (let entry of Object.entries(this.stages)) {
            entry[1].allCommandList.forEach((index) => {
                if(this.commands.at(index).command.point && this.commands.at(index).latlng.lat != 0 && this.commands.at(index).latlng.lng != 0){
                    const marker = new CircleMarker(this.commands.at(index).latlng, {radius: 5}).bindTooltip(entry[0], {permanent: false,});
                    this.drawAsLabeledLayer(entry[0], marker);

                    this.getAdjacentsTo(this.commands.at(index)).forEach((adjacent) => {
                        if (!adjacent.explored) {
                            // Draw edge
                            if(adjacent.command.point && adjacent.latlng.lat != 0 && adjacent.latlng.lng != 0) {
                                const layer = new Polyline([this.commands.at(index).latlng, adjacent.latlng]);
                                this.drawAsLabeledLayer(entry[0], layer);
                                this.commands.at(index).explored = true;
                            } else if(this.commands.at(adjacent.command.index + 1).command.point && this.commands.at(adjacent.command.index + 1).latlng.lat != 0 && this.commands.at(adjacent.command.index + 1).latlng.lng != 0){
                                const layer = new Polyline([this.commands.at(index).latlng, this.commands.at(adjacent.command.index + 1).latlng]);
                                this.drawAsLabeledLayer(entry[0], layer);
                                this.commands.at(index).explored = true;
                            } else if(this.commands.at(adjacent.command.index + 2).command.point && this.commands.at(adjacent.command.index + 2).latlng.lat != 0 && this.commands.at(adjacent.command.index + 2).latlng.lng != 0){
                                const layer = new Polyline([this.commands.at(index).latlng, this.commands.at(adjacent.command.index + 2).latlng]);
                                this.drawAsLabeledLayer(entry[0], layer);
                                this.commands.at(index).explored = true;
                            }
                        }
                    });
                }
            });
        }

        const colors = ['FF595E', 'FFCA3A', '8AC926', '1982C4', '6A4C93'];
        Object.values(this.layersControl.overlays).forEach((layer, idx) => {
            layer.setStyle({ color: `#${colors[idx % 5]}` });
        });
    }
}
